import { VersionedStore } from './versionedStore';
import { makeTextureAtlas } from './atlasCreator';
import { getLoadedImage } from './utils';
export class AtlasParser {
    atlasJson;
    latestImage;
    legacyImage;
    atlasStore;
    atlasHasLegacyImage;
    constructor(atlasJson, latestImage, legacyImage) {
        this.atlasJson = atlasJson;
        this.latestImage = latestImage;
        this.legacyImage = legacyImage;
        this.atlasStore = new VersionedStore();
        this.atlasStore.inclusive = false;
        const itemsAtlases = atlasJson;
        const addByVersion = (store, version, textures, imageName) => {
            for (const [key, path] of Object.entries(textures)) {
                store.push(version, key, { ...path, version, imageType: imageName });
            }
        };
        addByVersion(this.atlasStore, 'latest', itemsAtlases.latest.textures, 'latest');
        this.atlasHasLegacyImage = !!legacyImage;
        if (legacyImage && itemsAtlases.legacy) {
            for (const key of Object.keys(itemsAtlases.legacy.textures)) {
                const [version, name] = key.split('/');
                addByVersion(this.atlasStore, version, { [name]: itemsAtlases.legacy.textures[key] }, 'legacy');
            }
        }
    }
    get atlas() {
        return this.atlasJson;
    }
    getTextureInfo(itemName, version = 'latest') {
        const info = this.atlasStore.get(version, itemName);
        if (!info)
            return;
        const defaultSuSv = (info.imageType === 'latest' ? this.atlas.latest : this.atlas.legacy).suSv;
        return {
            ...info,
            su: info?.su ?? defaultSuSv,
            sv: info?.sv ?? defaultSuSv,
            getLoadedImage: async () => {
                return await getLoadedImage(info.imageType === 'latest' ? this.latestImage : this.legacyImage);
            }
        };
    }
    async makeNewAtlas(version, getCustomImage, _unusedTileSize = this.atlas.latest.tileSize, getTextureSortRankOrTopTextures, addTextures = []) {
        const itemsAtlases = this.atlasJson;
        const newTextures = {};
        const legacyImg = this.atlasHasLegacyImage ? await getLoadedImage(this.legacyImage) : null;
        const latestImg = await getLoadedImage(this.latestImage);
        for (const itemName of new Set([...Object.keys(itemsAtlases.latest.textures), ...addTextures])) {
            const customImage = getCustomImage?.(itemName);
            if (customImage === false)
                continue;
            if (customImage && customImage !== true) {
                const img = typeof customImage === 'string' ? await getLoadedImage(customImage) : customImage;
                newTextures[itemName] = {
                    u: 0,
                    v: 0,
                    su: 1,
                    sv: 1,
                    img
                };
                continue;
            }
            const info = this.getTextureInfo(itemName, version);
            if (!info)
                throw new Error(`Missing texture info from the provided atlas for ${itemName} and not custom data is provided`);
            const { u, v, su, sv } = info;
            const atlas = info.version === 'latest' ? itemsAtlases.latest : itemsAtlases.legacy;
            const image = info.imageType === 'latest' ? latestImg : legacyImg;
            if (!image)
                throw new Error(`Missing image for ${itemName}`);
            newTextures[itemName] = {
                u,
                v,
                su: su ?? atlas.suSv,
                sv: sv ?? atlas.suSv,
                img: image
            };
        }
        const newTexturesKeys = Object.keys(newTextures);
        if (getTextureSortRankOrTopTextures) {
            const getRankFn = typeof getTextureSortRankOrTopTextures === 'function' ? getTextureSortRankOrTopTextures : (key) => getTextureSortRankOrTopTextures.includes(key) ? 1 : -1;
            newTexturesKeys.sort((a, b) => getRankFn(b) - getRankFn(a));
        }
        const { json, canvas } = makeTextureAtlas({
            input: newTexturesKeys,
            getLoadedImage: (name) => {
                const texture = newTextures[name];
                const image = texture.img;
                const imgWidth = image.width;
                const imgHeight = image.height;
                //@ts-ignore we no longer need the image, remove from atlas
                delete texture.img;
                return {
                    image,
                    renderWidth: texture.su * imgWidth,
                    renderHeight: texture.sv * imgHeight,
                    renderSourceStartX: texture.u * imgWidth,
                    renderSourceStartY: texture.v * imgHeight,
                    renderSourceWidth: texture.su * imgWidth,
                    renderSourceHeight: texture.sv * imgHeight,
                };
            },
            tileSize: this.atlas.latest.tileSize,
        });
        let _newAtlasParser;
        return {
            canvas,
            atlas: json,
            get newAtlasParser() {
                if (!_newAtlasParser) {
                    _newAtlasParser = new AtlasParser({ latest: json }, canvas.toDataURL());
                }
                return _newAtlasParser;
            },
            get image() {
                return canvas.toDataURL();
            }
        };
    }
}
